import createImageUrlBuilder from '@sanity/image-url';
import type { ImageLoader, ImageLoaderProps } from 'next/image';
import type { Image } from 'sanity';

import { dataset, projectId } from '../env';

export type SanityImageSource = Image | string | null | undefined;

export interface SanityImageUrlOptions {
    auto?: boolean;
    blur?: number;
    dpr?: number;
    fit?: 'clip' | 'crop' | 'fill' | 'fillmax' | 'max' | 'min' | 'scale';
    format?: 'jpg' | 'png' | 'webp';
    height?: number;
    quality?: number;
    width?: number;
}

const imageBuilder = createImageUrlBuilder({
    projectId: projectId || '',
    dataset: dataset || '',
});

const DEFAULT_FIT: NonNullable<SanityImageUrlOptions['fit']> = 'max';

const roundDimension = (value: number) => Math.max(1, Math.round(value));

const isSanityAssetUrl = (source: string) => {
    try {
        const url = new URL(source);

        return (
            url.hostname === 'cdn.sanity.io' &&
            url.pathname.startsWith('/images/')
        );
    } catch {
        return false;
    }
};

const applyUrlOptions = (
    source: string,
    {
        auto = true,
        blur,
        dpr,
        fit = DEFAULT_FIT,
        format,
        height,
        quality,
        width,
    }: SanityImageUrlOptions,
) => {
    if (!isSanityAssetUrl(source)) {
        return source;
    }

    const url = new URL(source);

    if (auto) {
        url.searchParams.set('auto', 'format');
    }

    url.searchParams.set('fit', fit);

    if (format) {
        url.searchParams.set('fm', format);
    }

    if (width) {
        url.searchParams.set('w', String(roundDimension(width)));
    }

    if (height) {
        url.searchParams.set('h', String(roundDimension(height)));
    }

    if (quality) {
        url.searchParams.set('q', String(roundDimension(quality)));
    }

    if (blur) {
        url.searchParams.set('blur', String(roundDimension(blur)));
    }

    if (dpr) {
        url.searchParams.set('dpr', String(roundDimension(dpr)));
    }

    return url.toString();
};

const applyBuilderOptions = (
    source: Image,
    {
        auto = true,
        blur,
        dpr,
        fit = DEFAULT_FIT,
        format,
        height,
        quality,
        width,
    }: SanityImageUrlOptions,
) => {
    let builder = imageBuilder.image(source).fit(fit);

    if (width) {
        builder = builder.width(roundDimension(width));
    }

    if (height) {
        builder = builder.height(roundDimension(height));
    }

    if (quality) {
        builder = builder.quality(roundDimension(quality));
    }

    if (blur) {
        builder = builder.blur(roundDimension(blur));
    }

    if (dpr) {
        builder = builder.dpr(roundDimension(dpr));
    }

    if (auto) {
        builder = builder.auto('format');
    }

    if (format) {
        builder = builder.format(format);
    }

    return builder;
};

export const urlForImage = (
    source: SanityImageSource,
    options: SanityImageUrlOptions = {},
) => {
    if (!source) {
        return '';
    }

    if (typeof source === 'string') {
        return applyUrlOptions(source, options);
    }

    return applyBuilderOptions(source, options).url();
};

interface SanityImageLoaderOptions extends SanityImageUrlOptions {
    maxWidth?: number;
    resolveQuality?: (...args: [number]) => number;
}

export const createSanityImageLoader = (
    source: SanityImageSource,
    {
        maxWidth,
        quality,
        resolveQuality,
        ...baseOptions
    }: SanityImageLoaderOptions = {},
): ImageLoader => {
    return ({ width, quality: nextQuality }: ImageLoaderProps) => {
        const resolvedWidth = maxWidth
            ? Math.min(roundDimension(width), roundDimension(maxWidth))
            : roundDimension(width);
        const resolvedQuality =
            nextQuality ?? quality ?? resolveQuality?.(resolvedWidth);

        return urlForImage(source, {
            ...baseOptions,
            quality: resolvedQuality,
            width: resolvedWidth,
        });
    };
};

export const urlFor = (source: Image) =>
    applyBuilderOptions(source, { format: 'webp' });
